import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip,
} from "recharts";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2, ExternalLink } from "lucide-react";

const recColor = {
  ADOTTARE: "bg-[#00A859] text-white",
  MANTENERE: "bg-primary text-white",
  MIGLIORARE: "bg-[#F59E0B] text-white",
  SOSTITUIRE: "bg-destructive text-white",
};

export default function ToolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [history, setHistory] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const { canEdit } = useAuth();

  const load = async () => {
    const [t, c, h] = await Promise.all([api.get(`/tools/${id}`), api.get("/criteria"), api.get(`/tools/${id}/history`)]);
    setTool(t.data);
    setCriteria(c.data);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, [id]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/tools/${id}/analyze`);
      setTool((prev) => ({ ...prev, ai_analysis: data }));
      toast.success("Analisi AI completata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAnalyzing(false);
    }
  };

  if (!tool) {
    return <Layout><PageHeader title="Tool" /><div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div></Layout>;
  }

  const radarData = criteria.map((c) => ({ criterio: c.name, punteggio: tool.scores?.[c.id] ?? 0 }));
  const ai = tool.ai_analysis;

  return (
    <Layout>
      <PageHeader title={tool.name} subtitle={tool.category || "Tool IT"}>
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/tools")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
        </Button>
        {canEdit && (
          <Button data-testid="analyze-tool-btn" className="rounded-none" onClick={analyze} disabled={analyzing}>
            {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Analisi AI
          </Button>
        )}
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-border p-6">
          <div className="data-label">Punteggio complessivo</div>
          <div className="font-mono font-medium text-5xl mt-2">{tool.overall_score}</div>
          <div className="text-xs text-muted-foreground">su scala 0–10</div>
          <div className="mt-6 space-y-3 border-t border-border pt-4">
            <Row label="Vendor" value={tool.vendor || "—"} />
            <Row label="Costo mensile" value={`€ ${tool.cost_monthly || 0}`} />
            <Row label="Categoria" value={tool.category || "—"} />
            {tool.url && (
              <a href={tool.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                Sito web <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          {tool.description && <p className="mt-4 text-sm text-muted-foreground border-t border-border pt-4">{tool.description}</p>}
        </div>

        <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="tool-radar">
          <h3 className="font-heading font-bold text-lg tracking-tight mb-2">Profilo di valutazione</h3>
          <div className="data-label mb-4">Punteggio per criterio</div>
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={340}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="criterio" tick={{ fontSize: 12, fill: "#4B5563" }} />
                <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                <Radar dataKey="punteggio" stroke="#002FA7" fill="#002FA7" fillOpacity={0.25} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground">Nessun criterio.</p>}
        </div>

        <div className="lg:col-span-3 bg-white border border-border p-6" data-testid="tool-trend">
          <h3 className="font-heading font-bold text-lg tracking-tight mb-2">Andamento punteggio</h3>
          <div className="data-label mb-4">Storico del punteggio complessivo</div>
          {history.length >= 2 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={history.map((h) => ({ data: new Date(h.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }), punteggio: h.overall_score }))}>
                <XAxis dataKey="data" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                <YAxis domain={[0, 10]} tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                <RTooltip contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Line type="monotone" dataKey="punteggio" stroke="#002FA7" strokeWidth={2} dot={{ r: 3, fill: "#002FA7" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground">Lo storico si popola automaticamente a ogni modifica dei punteggi. Servono almeno 2 rilevazioni per visualizzare il trend.</p>
          )}
        </div>

        <div className="lg:col-span-3 bg-white border border-border p-6" data-testid="ai-analysis-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-bold text-lg tracking-tight">Analisi AI</h3>
          </div>
          {!ai ? (
            <p className="text-sm text-muted-foreground">Nessuna analisi generata. {canEdit ? 'Clicca "Analisi AI" per ottenere punti di forza, debolezze e una raccomandazione basata su Claude Sonnet 4.6.' : "Chiedi a un membro o amministratore di generarla."}</p>
          ) : (
            <div className="space-y-6">
              {ai.recommendation && (
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-xs font-bold tracking-wide ${recColor[ai.recommendation] || "bg-secondary"}`} data-testid="ai-recommendation">{ai.recommendation}</span>
                  <span className="text-sm text-muted-foreground">{ai.recommendation_reason}</span>
                </div>
              )}
              <p className="text-sm">{ai.summary}</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="data-label mb-2 text-[#00A859]">Punti di forza</div>
                  <ul className="space-y-1 text-sm list-disc pl-4">{(ai.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
                <div>
                  <div className="data-label mb-2 text-destructive">Debolezze</div>
                  <ul className="space-y-1 text-sm list-disc pl-4">{(ai.weaknesses || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium font-mono">{value}</span>
    </div>
  );
}
